import React, { useCallback } from 'react';

import { useMutation, UseMutationResult } from '@tanstack/react-query';

import { FileEntity, IFileDto } from '@/shared/interfaces';
import { mapIFileDto2FileEntity } from './mapIFileDto2FileEntity';
import { useFiles } from './useFiles';
import {
    sendDoctorFile,
    sendPatientFile,
    sendReferralFile,
} from '../api/sendFile';
import { deleteFile } from '../api/deleteFile';

type UploadFilesProps = {
    className?: string;
    initialFiles?: IFileDto[];
    senderType?: 'PATIENT' | 'CAREGIVER';
    uploadType?: 'STANDARD' | 'REFERRAL';
    fileConstraints?: {
        maxFileSizeMb?: number;
        maxFileCount?: number;
        maxTotalSizeMb?: number;
    };
    onFilesRejected?: (rejectedCount: number) => void;
};

export type UploadFilesReturn = {
    files: FileEntity[];
    resetFiles: () => void;
    deleteFileOnServer: UseMutationResult<any, Error, string, unknown>;
    removeFileFromList: (identifier: string) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
export const useUploadFiles = (props?: UploadFilesProps): UploadFilesReturn => {
    const initial = props?.initialFiles
        ? mapIFileDto2FileEntity(props.initialFiles)
        : [];

    const {
        files,
        resetFiles,
        appendFiles,
        setUploaded,
        setFailed,
        removeFile,
    } = useFiles({
        initialFiles: initial,
        maxFileSizeMb: props?.fileConstraints?.maxFileSizeMb,
        maxFileCount: props?.fileConstraints?.maxFileCount,
        maxTotalSizeMb: props?.fileConstraints?.maxTotalSizeMb,
    });

    const uploadMutation = useMutation<string, Error, FileEntity>({
        mutationFn:
            props?.senderType == 'PATIENT'
                ? sendPatientFile
                : props?.uploadType === 'REFERRAL'
                  ? sendReferralFile
                  : sendDoctorFile,
        onSuccess: (guid, fileEntity) => {
            setUploaded({ identifier: fileEntity.identifier, guid });
        },
        onError: (_err, fileEntity) => {
            setFailed(fileEntity.identifier);
        },
    });
    const deleteFileOnServer = useMutation<string, Error, string>({
        mutationFn: deleteFile,
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const list = e.target.files;
            if (!list) return;
            const newEntities = appendFiles(list);
            const rejectedCount = list.length - newEntities.length;
            if (rejectedCount > 0) {
                props?.onFilesRejected?.(rejectedCount);
            }
            // kick off upload immediately
            newEntities.forEach((ent) => uploadMutation.mutate(ent));
        },
        [appendFiles, uploadMutation, props?.onFilesRejected],
    );

    const removeFileFromList = useCallback(
        (identifier: string) => {
            const toDelete = files.find((f) => f.identifier === identifier);
            if (toDelete && toDelete.guid) {
                deleteFileOnServer.mutate(toDelete.guid);
            }
            removeFile(identifier);
        },
        [files, removeFile, deleteFileOnServer],
    );

    return {
        files,
        resetFiles,
        deleteFileOnServer,
        removeFileFromList,
        handleFileChange,
    };
};
